import Close from '@mui/icons-material/Close';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import { BackButton, PrimaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import { type FC, useEffect, useState } from 'react';
import { useGetAllConversationEvalsQuery, useRunConversationEvalMutation } from 'services/api/conversationEvals.api';
import { useBusinessUnitPromptQuery } from 'services/api/playground.api';
import type { RunConversationEvalResponse } from 'services/types/conversationEvals';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY } from 'styles/tokens';
import { AI_MODELS, DEFAULT_AI_MODEL, DEFAULT_EVALUATION_AI_MODEL } from 'utils/aiModels';

import { useEvalSource } from '../hooks/useEvalSource';
import EvalRunResults from './EvalRunResults';

interface RunEvalsPanelProps {
  open: boolean;
  onClose: () => void;
  businessUnitId: number | null;
  messageId?: string;
}

type PromptSource = 'live' | 'override';

const sectionLabel = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: alpha(NAVY, 0.5),
};

const bodyText = {
  fontFamily: 'Jost, sans-serif',
  fontSize: 13,
  color: NAVY,
};

const RunEvalsPanel: FC<RunEvalsPanelProps> = ({ open, onClose, businessUnitId, messageId }) => {
  const source = useEvalSource(open, messageId, businessUnitId);
  const targetBusinessUnitId = source.businessUnitIds[0] ?? null;

  const { data: businessUnitPrompt } = useBusinessUnitPromptQuery(
    targetBusinessUnitId ? { businessUnitId: targetBusinessUnitId } : skipToken
  );
  const { data: evals } = useGetAllConversationEvalsQuery(
    targetBusinessUnitId ? { businessUnitIds: [targetBusinessUnitId] } : skipToken
  );
  const [runConversationEval, { isLoading: running }] = useRunConversationEvalMutation();

  const [promptSource, setPromptSource] = useState<PromptSource>('live');
  const [overridePrompt, setOverridePrompt] = useState('');
  const [completionAIModel, setCompletionAIModel] = useState(DEFAULT_AI_MODEL);
  const [evaluationAIModel, setEvaluationAIModel] = useState(DEFAULT_EVALUATION_AI_MODEL);
  const [result, setResult] = useState<RunConversationEvalResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Seed the editable template once it arrives, without clobbering a rewrite already in progress.
  useEffect(() => {
    if (businessUnitPrompt?.rawPrompt) {
      setOverridePrompt((current) => current || businessUnitPrompt.rawPrompt || '');
    }
  }, [businessUnitPrompt?.rawPrompt]);

  const caseCount = evals?.length ?? 0;

  const handleRun = async () => {
    if (!targetBusinessUnitId) {
      return;
    }
    setRunError(null);
    setResult(null);
    try {
      const response = await runConversationEval({
        businessUnitIds: [targetBusinessUnitId],
        completionAIModel,
        evaluationAIModel,
        ...(promptSource === 'override' && overridePrompt.trim() ? { agentPrompt: overridePrompt } : {}),
      }).unwrap();
      setResult(response);
    } catch {
      setRunError('La corrida falló. Revisa que la unidad tenga evals guardados e inténtalo otra vez.');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 'min(560px, 100%)',
            background: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            display: 'flex',
            flexDirection: 'column',
            p: 2.5,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrowRounded sx={{ fontSize: 22, color: NAVY }} />
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 16, color: NAVY }}>
            Correr evals
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: alpha(NAVY, 0.6) }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Typography sx={{ ...bodyText, fontSize: 12.5, color: alpha(NAVY, 0.55), mb: 2 }}>
        Corre los casos guardados de esta unidad y mide si el asistente sigue respondiendo bien. Puedes probar una
        reescritura del prompt sin publicarla.
      </Typography>

      <Box sx={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {source.failed && (
          <InlineFeedback severity="warning">
            No pudimos identificar la unidad de negocio de esta conversación.
          </InlineFeedback>
        )}
        {runError && <InlineFeedback severity="error">{runError}</InlineFeedback>}

        <Box>
          <Typography sx={{ ...sectionLabel, mb: 1 }}>Prompt a evaluar</Typography>
          <RadioGroup value={promptSource} onChange={(e) => setPromptSource(e.target.value as PromptSource)}>
            <FormControlLabel
              value="live"
              control={<Radio size="small" sx={{ color: alpha(NAVY, 0.4), '&.Mui-checked': { color: NAVY } }} />}
              label="El prompt publicado de la unidad"
              slotProps={{ typography: { sx: bodyText } }}
            />
            <FormControlLabel
              value="override"
              control={<Radio size="small" sx={{ color: alpha(NAVY, 0.4), '&.Mui-checked': { color: NAVY } }} />}
              label="Una plantilla editada (sin publicar)"
              slotProps={{ typography: { sx: bodyText } }}
            />
          </RadioGroup>

          {promptSource === 'override' && (
            <Box sx={{ mt: 1 }}>
              <FormField
                label="Plantilla"
                multiline
                minRows={8}
                maxRows={18}
                value={overridePrompt}
                onChange={(e) => setOverridePrompt(e.target.value)}
                hint="Se interpola con el cliente de cada caso, igual que el prompt publicado. Deja los placeholders intactos."
                sx={{ '& .MuiOutlinedInput-input': { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }}
              />
            </Box>
          )}
        </Box>

        <FormField
          select
          label="Modelo del asistente"
          value={completionAIModel}
          onChange={(e) => setCompletionAIModel(e.target.value)}
        >
          {AI_MODELS.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </FormField>

        <FormField
          select
          label="Modelo del juez"
          value={evaluationAIModel}
          onChange={(e) => setEvaluationAIModel(e.target.value)}
          hint="Compara la respuesta contra lo que describiste al crear cada eval."
        >
          {AI_MODELS.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </FormField>

        {result && <EvalRunResults result={result} promptWasOverridden={promptSource === 'override'} />}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 2 }}>
        <Typography sx={{ ...bodyText, fontSize: 12, color: alpha(NAVY, 0.5) }}>
          {caseCount > 0 ? `${caseCount} evals en esta unidad` : 'Sin evals guardados todavía'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <BackButton onClick={onClose}>Cerrar</BackButton>
          <PrimaryButton
            onClick={handleRun}
            disabled={running || !targetBusinessUnitId || caseCount === 0}
            startIcon={running ? <CircularProgress size={14} sx={{ color: NAVY }} /> : undefined}
          >
            {running ? 'Corriendo…' : 'Correr'}
          </PrimaryButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default RunEvalsPanel;
